/*@flow*/
import type { Node } from 'react';
import { useContext, createElement, useState, useEffect } from 'react';
import FormContext from '../context';
import usePrev from 'lib/usePrev';
import same from 'lib/same';
import hash from 'lib/hash';
import * as t from 'lib/t';
import type { ComponentPropsT, LocalsT } from '../types';
import { move } from '../util';
import getTransformer from '../util/getTransformer';
import getTemplate from '../util/getTemplate';
import { useListItems, useLocals, useField } from '../hooks';
import UIDGenerator from '../util/UIDGenerator';

function toSameLength(value: any, keys: any[], uidGenerator: UIDGenerator) {
  if (value.length <= keys.length) {
    return keys;
  }
  const ret = [];
  value.forEach((_, i) => {
    ret[i] = keys[i] || uidGenerator.next();
  });
  return ret;
}

const isPresent = (v) => !t.Nil.is(v);

export default function ListComponent(props: ComponentPropsT): Node {
  const { path } = props;
  let { fieldOptions, fieldValue = [] } = useField<any[]>(path);
  fieldValue = Array.isArray(fieldValue) ? fieldValue : [];
  const transName = 'list';
  const transformer = getTransformer(fieldOptions, transName);
  const locals: $Shape<LocalsT> = useLocals(path, { transformer });
  const ctx = useContext(FormContext);
  const { uidGenerator } = ctx.state;
  const { disableAdd } = fieldOptions;
  const [keys, setKeys] = useState(fieldValue.map(() => uidGenerator.next()));
  const prevKeys = usePrev(keys);

  useEffect(() => {
    setKeys(toSameLength(fieldValue, keys, uidGenerator));
  }, [hash(fieldValue)]);

  function updateState(newKeys) {
    if (prevKeys && prevKeys.length > 0 && same(prevKeys, newKeys)) return;
    let newIx = newKeys.map((_, i) => i);

    if (prevKeys && prevKeys.length) {
      newIx = prevKeys.map((v2) => newKeys.indexOf(v2)).filter((v) => v !== -1);
    }

    const v3 = fieldValue;
    const newV = v3.map((k2, i) => v3[newIx[i]]).filter(isPresent);

    locals.onChange(newV);

    setKeys(newKeys);
  }

  let add;
  if (!disableAdd) {
    const onAdd = () => {
      const v = keys;
      updateState(v.concat([uidGenerator.next()]));
    };
    add = {
      type: 'add',
      label: 'Add',
      click: onAdd,
    };
  }
  const onUp = (key, i) => {
    const v = keys;
    updateState(move(v, v.indexOf(key), Math.max(0, i - 1)));
  };
  const onDown = (key, i) => {
    const v = keys;
    updateState(move(v, v.indexOf(key), Math.min(v.length - 1, i + 1)));
  };
  const onRemove = (key) => {
    updateState(keys.filter((v2) => v2 !== key));
  };

  const items = useListItems(path, keys, onUp, onDown, onRemove);

  const template = getTemplate(fieldOptions, 'list');

  return createElement(template, { ...locals, add, items });
}
